import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import LoanDetailsStep from './LoanDetailsStep';
import { SecurityAvailability } from '../../../services/schemas/loanServiceSchemas';
import { OFFERED_SECURITY_TYPES, securityTypeLabel } from '../../../constants/securityTypes';

/**
 * Step 4 - the funding request.
 *
 * Captures what the customer is actually asking for at origination, early in the application
 * rather than after the credit work has started. The commercial terms and the loan calculator
 * are rendered by LoanDetailsStep below, so the amount, product, purpose and term are captured
 * once here instead of being repeated later in the wizard.
 */


const AVAILABILITY_LABELS: Record<string, string> = {
  [SecurityAvailability.YES]: 'Yes',
  [SecurityAvailability.NO]: 'No',
  [SecurityAvailability.TO_BE_CONFIRMED]: 'To be confirmed'
};

const FundingRequestStep: React.FC = () => {
  const { register, watch, setValue } = useFormContext();

  const availability = (watch('loan.securityAvailability' as any) as string) || '';
  const offered = (watch('loan.securityTypesOffered' as any) as string[]) || [];

  /**
   * securityType is the decision that actually routes the application: UNSECURED skips the
   * Collateral step, and any collateral captured under it is dropped on submit. Declaring
   * security offered against an unsecured loan therefore says something the application cannot
   * hold, so the declaration is only asked for once the deal is structured as secured.
   */
  const securityType = watch('loan.securityType' as any) as string;
  const isUnsecured = securityType === 'UNSECURED';

  useEffect(() => {
    if (isUnsecured && (availability || offered.length)) {
      setValue('loan.securityAvailability' as any, '', { shouldDirty: true });
      setValue('loan.securityTypesOffered' as any, [], { shouldDirty: true });
    }
  }, [isUnsecured, availability, offered.length, setValue]);

  const toggleSecurityType = (value: string) => {
    const next = offered.includes(value)
      ? offered.filter((v) => v !== value)
      : [...offered, value];
    setValue('loan.securityTypesOffered' as any, next, { shouldDirty: true });
  };

  const setAvailability = (value: string) => {
    setValue('loan.securityAvailability' as any, value, { shouldDirty: true });
    // Security types only make sense once the customer has said something is available.
    if (value !== SecurityAvailability.YES) {
      setValue('loan.securityTypesOffered' as any, [], { shouldDirty: true });
    }
  };

  return (
    <div className="funding-request-step">
      <h5 className="mb-1">Loan / Funding Request</h5>
      <p className="text-muted mb-4">
        Capture what the customer needs before requesting detailed financial or product information.
      </p>

      {/* Amount, funding product, purpose, term and the loan calculation */}
      <LoanDetailsStep />

      <hr className="my-4" />

      <h6 className="mb-3">Funding request</h6>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="form-group">
            <label htmlFor="requiredFundingDate">Required funding date</label>
            <input
              type="date"
              id="requiredFundingDate"
              className="form-control"
              {...register('loan.requiredFundingDate' as const)}
            />
            <small className="text-muted">When the customer needs the money.</small>
          </div>
        </div>

        <div className="col-md-6">
          <div className="form-group">
            <label htmlFor="proposedRepaymentDate">Proposed repayment date</label>
            <input
              type="date"
              id="proposedRepaymentDate"
              className="form-control"
              {...register('loan.proposedRepaymentDate' as const)}
            />
            <small className="text-muted">When the customer expects to repay.</small>
          </div>
        </div>

        <div className="col-md-6">
          <div className="form-group">
            <label htmlFor="customerContribution">Customer contribution (R)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              id="customerContribution"
              className="form-control"
              {...register('loan.customerContribution' as const, {
                setValueAs: (v: any) => (v === '' || v === null ? undefined : Number(v))
              })}
            />
            <small className="text-muted">Where applicable.</small>
          </div>
        </div>

        <div className="col-md-6">
          <div className="form-group">
            <label htmlFor="primaryRepaymentSource">Primary repayment source</label>
            <input
              type="text"
              id="primaryRepaymentSource"
              className="form-control"
              placeholder="e.g. Payment from Valterra Platinum Ltd"
              {...register('loan.primaryRepaymentSource' as const)}
            />
          </div>
        </div>
      </div>

      {!isUnsecured && (
        <>
      <hr className="my-4" />

      <div className="d-flex justify-content-between align-items-start mb-1">
        <h6 className="mb-0">Security offered at origination</h6>
        <span className="badge bg-light text-dark border">Initial declaration</span>
      </div>
      <p className="text-muted small">
        Capture only what the customer says is available. Credit will assess it in detail later.
      </p>

      <div className="form-group mb-3">
        <label className="d-block">Security / Collateral available?</label>
        <div className="btn-group" role="group" aria-label="Security availability">
          {Object.keys(AVAILABILITY_LABELS).map((value) => (
            <button
              key={value}
              type="button"
              className={`btn btn-sm ${availability === value ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setAvailability(value)}
            >
              {availability === value ? '✓ ' : ''}{AVAILABILITY_LABELS[value]}
            </button>
          ))}
        </div>
      </div>

      {availability === SecurityAvailability.YES && (
        <div className="form-group">
          <label className="d-block">Type of security offered</label>
          <div className="d-flex flex-wrap gap-2">
            {/* The manual's security types, plus anything an older application already chose (finding #6) */}
            {[...OFFERED_SECURITY_TYPES, ...offered.filter(v => !OFFERED_SECURITY_TYPES.includes(v))].map((value) => {
              const selected = offered.includes(value);
              return (
                <button
                  key={value}
                  type="button"
                  className={`btn btn-sm ${selected ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => toggleSecurityType(value)}
                >
                  {selected ? '✓ ' : '+ '}{securityTypeLabel(value)}
                </button>
              );
            })}
          </div>
          <small className="text-muted d-block mt-2">
            Later in the workflow: detailed security / collateral assessment &mdash; ownership,
            valuation, encumbrances, coverage, documents, legal perfection.
          </small>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default FundingRequestStep;
